import { FileText, FlaskConical, ImageIcon, Loader2, Paperclip, Pill } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { ArquivoDoProntuarioResponse, ExameDoProntuarioResponse } from '@/api/generated/model';
import {
  prontuariosControllerUrlDoArquivo,
  useProntuariosControllerAnexos,
} from '@/api/generated/prontuarios/prontuarios';
import { ApiError } from '@/api/http-client';
// Data de coleta é DATA (meia-noite UTC) — lida em UTC, senão no Brasil cai no dia anterior.
import { formatarData } from '@/features/exames/rotulos';
import { formatarDataHora } from '@/features/historico/rotulos';
import { ROTAS } from '@/shared/lib/rotas';
import { toast } from '@/shared/ui/sonner';

const ROTULO_ORIGEM: Record<string, string> = { MANUAL: 'Interno', VERTIS: 'Vertis', EXTERNO: 'Externo' };

/**
 * Fotos/PDFs e exames de um prontuário ou de um anexo (item 16). O arquivo abre
 * numa aba nova pela URL assinada, pedida só no clique — com dezenas de cards
 * na tela, assinar todas as URLs de antemão seria desperdício.
 */
export function ItensDoProntuario({
  prontuarioId,
  arquivos,
  exames,
}: {
  prontuarioId: string;
  arquivos: ArquivoDoProntuarioResponse[];
  exames: ExameDoProntuarioResponse[];
}) {
  const [abrindo, setAbrindo] = useState<string | null>(null);

  if (arquivos.length === 0 && exames.length === 0) return null;

  async function abrir(arquivoId: string) {
    setAbrindo(arquivoId);
    try {
      const { url } = await prontuariosControllerUrlDoArquivo(prontuarioId, arquivoId);
      window.open(url, '_blank', 'noopener');
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível abrir o arquivo.');
    } finally {
      setAbrindo(null);
    }
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {arquivos.map((arquivo) => (
        <button
          key={arquivo.arquivoId}
          type="button"
          onClick={() => void abrir(arquivo.arquivoId)}
          disabled={abrindo === arquivo.arquivoId}
          title={arquivo.nomeOriginal}
          className="inline-flex items-center gap-1 rounded-md border border-brand/20 px-2 py-1 text-xs text-brand hover:bg-brand/5 disabled:opacity-60"
        >
          {abrindo === arquivo.arquivoId ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : arquivo.mimeType === 'application/pdf' ? (
            <FileText className="w-3.5 h-3.5" />
          ) : (
            <ImageIcon className="w-3.5 h-3.5" />
          )}
          {arquivo.titulo}
        </button>
      ))}
      {exames.map((exame) => (
        <Link
          key={exame.exameId}
          to={ROTAS.exameDetalhe.replace(':id', exame.exameId)}
          className="inline-flex items-center gap-1 rounded-md border border-brand/20 px-2 py-1 text-xs text-brand hover:bg-brand/5"
        >
          <FlaskConical className="w-3.5 h-3.5" />
          {exame.nome}
          <span className="text-muted-foreground">
            · {formatarData(exame.coletadoEm)} ·{' '}
            {ROTULO_ORIGEM[exame.origem] ?? exame.origem}
          </span>
        </Link>
      ))}
    </div>
  );
}

/**
 * Os anexos de um prontuário — em ordem cronológica, cada um com título, data,
 * autor, texto, arquivos, exames e o que mudou no tratamento. Só busca quando
 * há anexo (`totalAnexos` vem na listagem).
 */
export function AnexosDoProntuario({ prontuarioId, totalAnexos }: { prontuarioId: string; totalAnexos: number }) {
  const { data, isPending, isError } = useProntuariosControllerAnexos(prontuarioId, {
    query: { enabled: totalAnexos > 0 },
  });

  if (totalAnexos === 0) return null;

  return (
    <div className="space-y-2 border-t border-brand/10 pt-2" aria-label="Anexos do prontuário">
      {isPending ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando anexos...
        </p>
      ) : isError || !data ? (
        <p className="text-xs text-destructive">Não foi possível carregar os anexos.</p>
      ) : (
        data.itens.map((anexo) => (
          <div key={anexo.id} className="space-y-1 rounded-md bg-brand/5 px-3 py-2">
            <p className="flex flex-wrap items-center gap-1.5 text-sm">
              <Paperclip className="w-3.5 h-3.5 text-brand" />
              <span className="font-medium text-foreground">{anexo.titulo}</span>
              <span className="text-xs text-muted-foreground">
                · {formatarDataHora(anexo.criadoEm)} · {anexo.autorNome ?? '—'}
              </span>
            </p>
            {anexo.conteudo && <p className="text-sm text-muted-foreground whitespace-pre-line">{anexo.conteudo}</p>}
            {anexo.resumoDoTratamento && (
              <p className="flex items-center gap-1.5 text-xs text-foreground">
                <Pill className="w-3.5 h-3.5 text-brand" /> {anexo.resumoDoTratamento}
              </p>
            )}
            <ItensDoProntuario prontuarioId={prontuarioId} arquivos={anexo.arquivos} exames={anexo.exames} />
          </div>
        ))
      )}
    </div>
  );
}
